import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaPencilAlt, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import Badge from '../../components/Badge';
import Button from "../../components/Button";
import Input from "../../components/Input";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { api } from "../../services/api";

const defaultColors = {
  primary: "#4F46E5",
  secondary: "#10B981",
  accent: "#F59E0B",
  background: "#ffffff",
  surface: "#ffffff",
  border: "#e5e7eb",
  text: "#1f2937",
};

const ColorSwatch = ({ color, title }) => (
  <div
    className="w-6 h-6 rounded-full border border-border"
    style={{ backgroundColor: color }}
    title={title}
  />
);

const PaletteLegend = ({ colors }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-text/70">
    {Object.entries(colors).map(([key, color]) => (
      <div key={key} className="flex items-center gap-2">
        <ColorSwatch color={color} title={key} />
        <div className="flex flex-col leading-tight">
          <span className="font-semibold capitalize text-text">{key}</span>
          <span className="font-mono text-[11px]">{color}</span>
        </div>
      </div>
    ))}
  </div>
);

const ColorInput = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-text opacity-70 capitalize mb-1">
      {label}
    </label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        name={name}
        value={value}
        onChange={onChange}
        className="h-10 w-20 rounded border border-border cursor-pointer"
      />
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        className="flex-1 p-2 border border-border rounded-md bg-surface text-text"
      />
    </div>
  </div>
);

const ThemeCard = ({
  theme,
  isUserTheme,
  isSystemActive,
  isDisabled = false,
  onApply,
  onSetSystemActive,
  onToggleEnabled,
  onEdit,
  onDelete,
  disableActions,
  isDefault = false,
  isAdmin = false,
}) => (
  <div
    className={`p-4 border rounded-lg shadow-sm bg-background transition hover:-translate-y-[1px] hover:shadow-md ${
      isDisabled
        ? "border-border bg-surface"
        : isUserTheme
        ? "border-primary ring-2 ring-primary/20"
        : isSystemActive
        ? "border-secondary ring-2 ring-secondary/20"
        : "border-border"
    }`}
  >
    <div className="relative overflow-hidden rounded-lg border border-border mb-3">
      <div
        className={`h-20 ${isDisabled ? "opacity-70" : ""}`}
        style={{
          background: `linear-gradient(120deg, ${
            theme.colors?.primary || defaultColors.primary
          }, ${theme.colors?.secondary || defaultColors.secondary})`,
        }}
      />
      {isDisabled && <div className="absolute inset-0 bg-surface/40" />}
      <div className="absolute inset-0 flex items-start justify-between p-3 pointer-events-none">
        <h3 className="text-base font-semibold text-white drop-shadow-md">
          {isDefault ? "Default Theme" : theme.name}
        </h3>
        <div className="flex flex-col gap-1 items-end">
          {isDisabled && (
            <Badge
              variant="error"
              label="Disabled"
              size="small"
              className="font-medium"
              aria-label="Theme Disabled"
            />
          )}
          {isUserTheme && (
            <Badge
              variant="info"
              label="Your Theme"
              size="small"
              className="font-medium"
              aria-label="Your Theme"
            />
          )}
          {isSystemActive && (
            <Badge
              variant="success"
              label="System Active"
              size="small"
              className="font-medium"
              aria-label="System Active"
            />
          )}
        </div>
      </div>
    </div>

    <div className={`mb-4 ${isDisabled ? "saturate-0 brightness-95" : ""}`}>
      <PaletteLegend colors={theme.colors || defaultColors} />
    </div>

    <div className="flex flex-col gap-2">
      {isAdmin ? (
        <div className="flex gap-2">
          {onApply && !isUserTheme && !isDisabled && (
            <Button
              onClick={onApply}
              disabled={disableActions}
              variant="outline"
              size="sm"
              className="w-full"
              aria-label="Apply Theme"
            >
              Apply to Me
            </Button>
          )}
          {onSetSystemActive && !isSystemActive && !isDisabled && (
            <Button
              onClick={onSetSystemActive}
              disabled={disableActions}
              variant="secondary"
              size="sm"
              className="w-full"
              aria-label="Set as System Theme"
            >
              Set as System Theme
            </Button>
          )}
        </div>
      ) : (
        onApply && !isUserTheme && !isDisabled && (
          <Button
            onClick={onApply}
            disabled={disableActions}
            variant="outline"
            size="sm"
            className="w-full"
            aria-label="Apply Theme"
          >
            Apply to Me
          </Button>
        )
      )}

      <div className="flex gap-2">
        {isAdmin && onToggleEnabled && !isDefault && (
          <Button
            onClick={onToggleEnabled}
            disabled={disableActions}
            variant={isDisabled ? "secondary" : "outline"}
            size="sm"
            className="flex-1"
            aria-label={isDisabled ? "Enable Theme" : "Disable Theme"}
          >
            {isDisabled ? "Enable" : "Disable"}
          </Button>
        )}

        {onEdit && !isDefault && !isDisabled && (
          <Button
            onClick={onEdit}
            disabled={disableActions}
            variant="ghost"
            size="sm"
            className="flex-1 flex items-center justify-center gap-2"
            aria-label="Edit Theme"
          >
            <FaPencilAlt className="w-3 h-3" />
            Edit
          </Button>
        )}

        {onDelete && !isUserTheme && !isSystemActive && !isDefault && (
          <Button
            onClick={onDelete}
            disabled={disableActions}
            variant="danger"
            size="sm"
            className="flex-1 flex items-center justify-center gap-2"
            aria-label="Delete Theme"
          >
            <FaTrash className="w-3 h-3" />
            Delete
          </Button>
        )}
      </div>
    </div>
  </div>
);

const ThemeForm = ({
  isCreating,
  themeName,
  setThemeName,
  colors,
  setColors,
  onSubmit,
  onCancel,
  loading,
}) => (
  <div className="mb-8 p-4 border border-border rounded-lg bg-background">
    <h3 className="text-lg font-semibold mb-4 text-text">
      {isCreating ? "Create New Theme" : "Edit Theme"}
    </h3>
    <form onSubmit={onSubmit} className="space-y-4">
      {isCreating && (
        <Input
          label="Theme Name"
          type="text"
          value={themeName}
          onChange={(e) => setThemeName(e.target.value)}
          required
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(colors).map(([key, value]) => (
          <ColorInput
            key={key}
            label={`${key} color`}
            name={key}
            value={value}
            onChange={(e) =>
              setColors((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }))
            }
          />
        ))}
      </div>

      <div className="flex gap-4 mt-6">
        <Button type="submit" disabled={loading} loading={loading}>
          Save Theme
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          disabled={loading}
          variant="secondary"
        >
          Cancel
        </Button>
      </div>
    </form>
  </div>
);

const ThemeSettings = () => {
  const { refetchTheme, currentThemeId } = useTheme();
  // toast is now used for notifications
  const { user } = useAuth();
  const [themes, setThemes] = useState([]);
  const [activeThemeId, setActiveThemeId] = useState(null);
  const isAdmin = user?.role === "admin";
  const [isCreating, setIsCreating] = useState(false);
  const [editingThemeId, setEditingThemeId] = useState(null);
  const [themeName, setThemeName] = useState("");
  const [colors, setColors] = useState(defaultColors);
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    themeId: null,
    action: null,
  });

  const resetForm = () => {
    setIsCreating(false);
    setEditingThemeId(null);
    setThemeName("");
    setColors(defaultColors);
  };


  // Debounce fetchThemes to avoid redundant API calls
  const fetchThemesTimeout = useRef();
  const fetchThemes = useCallback(() => {
    if (fetchThemesTimeout.current) {
      clearTimeout(fetchThemesTimeout.current);
    }
    fetchThemesTimeout.current = setTimeout(async () => {
      try {
        const response = await api.get(user?.role === "admin" ? "/theme/all" : "/theme");
        if (response.success) {
          setThemes(response.themes || []);
          setActiveThemeId(response.activeTheme?._id || null);
        }
      } catch (error) {
        toast.error("Failed to load themes.");
      }
    }, 250);
  }, [user]);



  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  const handleSave = async (e) => {
    e.preventDefault();

    if (isCreating && !themeName.trim()) {
      toast.error("Please provide a theme name.");
      return;
    }

    const hexPattern = /^#[0-9A-F]{6}$/i;
    const invalidColors = Object.entries(colors).filter(
      ([_, v]) => !hexPattern.test(v)
    );
    if (invalidColors.length > 0) {
      toast.error(
        `Invalid color: ${invalidColors.map(([k]) => k).join(", ")}`,
        "error"
      );
      return;
    }

    setLoading(true);

    try {
      if (isCreating) {
        const response = await api.post("/theme", {
          name: themeName.trim(),
          colors,
        });
        if (response?.theme?._id)
          setTimeout(() => handleApply(response.theme._id), 500);
        toast.success("Theme created successfully!");
      } else if (editingThemeId) {
        await api.put(`/theme/${editingThemeId}`, { colors });
        toast.success("Theme updated successfully!");
      }
      await fetchThemes();
      resetForm();
    } catch (error) {
      toast.error(
        error?.error || error?.message || "Failed to save theme.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (themeId) => {
    setLoading(true);
    try {
      await api.put("/theme/user/theme", { themeId });
      // Refetch theme from ThemeContext to apply the theme globally
      await refetchTheme();
      // Refetch all themes to update UI
      await fetchThemes();
      toast.success("Personal theme applied successfully!");
    } catch (error) {
      toast.error(
        error?.error || error?.message || "Failed to apply theme.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetSystemActive = async (themeId) => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      if (themeId === "default") {
        await api.post("/theme/reset");
        setActiveThemeId(null);
        toast.success("System theme reset to default.");
      } else {
        await api.post(`/theme/${themeId}/apply`);
        setActiveThemeId(themeId);
        toast.success("System-wide theme activated!");
      }
      await fetchThemes();
      await refetchTheme();
    } catch (error) {
      toast.error(
        error?.error || error?.message || "Failed to set system theme.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (themeId) => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.patch(`/theme/${themeId}/toggle`);
      toast.success(response.message || "Theme status updated.");
      await fetchThemes();
      await refetchTheme();
      if (userThemeId === themeId && !response.theme.isEnabled)
        await handleApply("default");
    } catch (error) {
      toast.error(
        error?.error || error?.message || "Failed to toggle theme.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (themeId) => {
    setLoading(true);
    try {
      await api.delete(`/theme/${themeId}`);
      toast.success("Theme deleted successfully.");
      await fetchThemes();
      await refetchTheme();
      if (userThemeId === themeId) await handleApply("default");
    } catch (error) {
      toast.error(
        error?.error || error?.message || "Failed to delete theme.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const defaultTheme = useMemo(() => ({ name: "Default Theme", colors: defaultColors }), []);
  const activeTheme = useMemo(() => (
    activeThemeId
      ? themes.find((t) => t._id === activeThemeId) || null
      : defaultTheme
  ), [activeThemeId, themes, defaultTheme]);

  // Use currentThemeId from ThemeContext for user's theme
  const userTheme = useMemo(() => (
    currentThemeId
      ? themes.find((t) => t._id === currentThemeId) || defaultTheme
      : defaultTheme
  ), [currentThemeId, themes, defaultTheme]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Theme Management"
        subtitle="Create and manage themes. Set system-wide default or let users choose personal preferences."
        action={
          <Button
            onClick={() => {
              resetForm();
              setIsCreating(true);
            }}
            disabled={loading || isCreating || editingThemeId}
          >
            <FaPlus className="w-4 h-4 mr-1" />
            Create New Theme
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 mb-6">
        <StatCard
          title="System Theme"
          value={activeTheme?.name || "Default Theme"}
          subtitle="Applies to everyone by default."
          color="secondary"
        />
        <StatCard
          title="Your Theme"
          value={userTheme?.name || "Default Theme"}
          subtitle="Personal preference just for you."
          color="primary"
        />
        <StatCard
          title="Themes Available"
          value={themes.length + 1}
          subtitle="Includes the default look."
          color="accent"
        />
      </div>

      {(isCreating || editingThemeId) && (
        <ThemeForm
          isCreating={isCreating}
          themeName={themeName}
          setThemeName={setThemeName}
          colors={colors}
          setColors={setColors}
          onSubmit={handleSave}
          onCancel={resetForm}
          loading={loading}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ThemeCard
          theme={defaultTheme}
          isDefault
          isUserTheme={currentThemeId === null}
          isSystemActive={activeThemeId === null}
          onApply={currentThemeId !== null ? () => handleApply("default") : null}
          onSetSystemActive={
            isAdmin && activeThemeId !== null
              ? () => handleSetSystemActive("default")
              : null
          }
          disableActions={loading}
          isAdmin={isAdmin}
        />

        {themes.map((theme) => (
          <ThemeCard
            key={theme._id}
            theme={theme}
            isUserTheme={currentThemeId === theme._id}
            isSystemActive={activeThemeId === theme._id}
            isDisabled={!theme.isEnabled}
            onApply={() => handleApply(theme._id)}
            onSetSystemActive={
              isAdmin ? () => handleSetSystemActive(theme._id) : null
            }
            onToggleEnabled={
              isAdmin ? () => handleToggleEnabled(theme._id) : null
            }
            onEdit={() => {
              setEditingThemeId(theme._id);
              setThemeName(theme.name);
              setColors(theme.colors || defaultColors);
            }}
            onDelete={() =>
              setConfirmModal({
                isOpen: true,
                themeId: theme._id,
                action: "delete",
              })
            }
            disableActions={loading}
            isAdmin={isAdmin}
          />
        ))}
      </div>
    </div>
  );
};

export default ThemeSettings;
